// Self-hosted analytics tracker — runs in the browser only.
// Public API: trackPageview(url, title), trackEvent(type, payload), getSessionId().

type EventPayload = Record<string, unknown>;

interface QueuedEvent {
  type: string;
  payload: EventPayload;
  timestamp: number;
}

interface SessionMeta {
  id: string;
  userId: string | null;
  device: "mobile" | "tablet" | "desktop";
  isNewVisitor: boolean;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  screenWidth: number;
  screenHeight: number;
}

const SESSION_KEY = "ns_analytics_session";
const SESSION_TIMESTAMP_KEY = "ns_analytics_session_last";
const VISITED_KEY = "ns_visited_before";
const QUEUE_KEY = "ns_analytics_queue";
const PENDING_BEACON_KEY = "ns_analytics_pending";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const BATCH_INTERVAL_MS = 5000;

let initialized = false;
let session: SessionMeta | null = null;
let lastPageviewAt = 0;
let lastPathname = "";
let lastPageviewMeta: { sessionId: string; pathname: string } | null = null;
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

// ─── helpers ──────────────────────────────────────────────────────
function shouldTrack(): boolean {
  if (typeof window === "undefined") return false;
  // Bots
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return false;
  // Admin / API routes
  if (typeof location !== "undefined") {
    const p = location.pathname;
    if (p.startsWith("/admin") || p.startsWith("/api")) return false;
  }
  return true;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try { return crypto.randomUUID(); } catch { /* fall through */ }
  }
  // RFC4122 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function detectDevice(): SessionMeta["device"] {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|Nexus 7|SM-T/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod|Mobile/i.test(ua)) return "mobile";
  return "desktop";
}

function getUtmParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  const params = new URLSearchParams(location.search);
  return {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
  };
}

function readUserId(): string | null {
  try {
    const v = document.cookie.split("; ").find((c) => c.startsWith("ns_user_id="));
    return v ? decodeURIComponent(v.split("=")[1] || "") || null : null;
  } catch {
    return null;
  }
}

function loadOrCreateSession(): SessionMeta {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const lastStr = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
    const last = lastStr ? Number(lastStr) : 0;
    if (raw && now - last < SESSION_TIMEOUT_MS) {
      const s = JSON.parse(raw) as SessionMeta;
      sessionStorage.setItem(SESSION_TIMESTAMP_KEY, String(now));
      return s;
    }
  } catch { /* ignore */ }

  let isNewVisitor = true;
  try {
    if (localStorage.getItem(VISITED_KEY)) isNewVisitor = false;
    else localStorage.setItem(VISITED_KEY, "1");
  } catch { /* ignore */ }

  const utm = getUtmParams();
  const meta: SessionMeta = {
    id: uuid(),
    userId: readUserId(),
    device: detectDevice(),
    isNewVisitor,
    ...utm,
    referrer: document.referrer || undefined,
    screenWidth: window.screen?.width || 0,
    screenHeight: window.screen?.height || 0,
  };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(meta));
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, String(now));
  } catch { /* ignore */ }
  return meta;
}

function touchSession(): void {
  try {
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

// ─── queue persistence ────────────────────────────────────────────
function persistQueue() {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)); } catch { /* ignore */ }
}
function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (raw) queue = JSON.parse(raw) as QueuedEvent[];
  } catch { queue = []; }
}

// ─── network ──────────────────────────────────────────────────────
async function postJson(url: string, body: unknown): Promise<boolean> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function beacon(url: string, body: unknown): boolean {
  try {
    if ("sendBeacon" in navigator) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      return navigator.sendBeacon(url, blob);
    }
  } catch { /* ignore */ }
  return false;
}

async function flushQueue(useBeacon = false): Promise<void> {
  if (!session || queue.length === 0) return;
  const batch = queue.slice();
  const body = { sessionId: session.id, userId: session.userId, events: batch };
  let ok = false;
  if (useBeacon) {
    ok = beacon("/api/analytics/event", body);
  } else {
    ok = await postJson("/api/analytics/event", body);
  }
  if (ok) {
    queue = queue.slice(batch.length);
    persistQueue();
  }
}

function startFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(() => { void flushQueue(false); }, BATCH_INTERVAL_MS);
}

// ─── pageview tracking ────────────────────────────────────────────
async function sendPageview(url: string, title: string): Promise<void> {
  if (!session) return;
  const pathname = new URL(url, location.origin).pathname;
  if (lastPathname === pathname && Date.now() - lastPageviewAt < 800) return;
  lastPathname = pathname;
  lastPageviewAt = Date.now();
  lastPageviewMeta = { sessionId: session.id, pathname };
  touchSession();
  const body = {
    sessionId: session.id,
    userId: session.userId,
    url,
    pathname,
    title,
    referrer: session.referrer || document.referrer || null,
    device: session.device,
    screenWidth: session.screenWidth,
    screenHeight: session.screenHeight,
    isNewVisitor: session.isNewVisitor,
    utmSource: session.utmSource || null,
    utmMedium: session.utmMedium || null,
    utmCampaign: session.utmCampaign || null,
    userAgent: navigator.userAgent.slice(0, 200),
    timestamp: Date.now(),
  };
  const ok = await postJson("/api/analytics/pageview", body);
  if (!ok) {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_BEACON_KEY) || "[]");
      pending.push(body);
      localStorage.setItem(PENDING_BEACON_KEY, JSON.stringify(pending.slice(-50)));
    } catch { /* ignore */ }
  }
}

async function retryPending(): Promise<void> {
  try {
    const raw = localStorage.getItem(PENDING_BEACON_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw) as unknown[];
    if (!Array.isArray(arr) || arr.length === 0) return;
    const remaining: unknown[] = [];
    for (const body of arr) {
      const ok = await postJson("/api/analytics/pageview", body);
      if (!ok) remaining.push(body);
    }
    localStorage.setItem(PENDING_BEACON_KEY, JSON.stringify(remaining));
  } catch { /* ignore */ }
}

// ─── duration tracking on unload ──────────────────────────────────
let pageEnterAt = 0;
function sendDuration(): void {
  if (!lastPageviewMeta || !session) return;
  const seconds = Math.max(0, Math.round((Date.now() - pageEnterAt) / 1000));
  if (seconds < 1) return;
  beacon("/api/analytics/pageview-duration", {
    sessionId: lastPageviewMeta.sessionId,
    pathname: lastPageviewMeta.pathname,
    durationSeconds: seconds,
  });
}

// ─── public api ───────────────────────────────────────────────────
export function getSessionId(): string | null {
  return session?.id || null;
}

export function trackPageview(url?: string, title?: string): void {
  if (!shouldTrack()) return;
  if (!initialized) initTracker();
  const finalUrl = url || location.href;
  const finalTitle = title || document.title || "";
  pageEnterAt = Date.now();
  void sendPageview(finalUrl, finalTitle);
}

export function trackEvent(type: string, payload: EventPayload = {}): void {
  if (!shouldTrack()) return;
  if (!initialized) initTracker();
  if (!session) return;
  queue.push({ type, payload, timestamp: Date.now() });
  persistQueue();
  touchSession();
}

export function initTracker(): void {
  if (initialized || !shouldTrack()) return;
  initialized = true;
  loadQueue();
  session = loadOrCreateSession();
  startFlushTimer();
  void retryPending();

  // On unload — send remaining events via beacon + page duration
  window.addEventListener("beforeunload", () => {
    sendDuration();
    if (queue.length > 0 && session) {
      const ok = beacon("/api/analytics/event", {
        sessionId: session.id,
        userId: session.userId,
        events: queue,
      });
      if (ok) { queue = []; persistQueue(); }
    }
  });

  // Detect quiz-in-progress on unload (consumers set window.__quizInProgress = quizId)
  window.addEventListener("beforeunload", () => {
    const inProgress = (window as unknown as { __quizInProgress?: string }).__quizInProgress;
    if (inProgress) {
      beacon("/api/analytics/event", {
        sessionId: session?.id,
        userId: session?.userId || null,
        events: [{ type: "quiz_abandoned", payload: { quizId: inProgress }, timestamp: Date.now() }],
      });
    }
  });

  // Visibility — flush when tab hidden
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendDuration();
      if (queue.length > 0 && session) {
        const ok = beacon("/api/analytics/event", {
          sessionId: session.id,
          userId: session.userId,
          events: queue,
        });
        if (ok) { queue = []; persistQueue(); }
      }
    }
  });
}

// Allow other modules to mark current user (used after login)
export function setUserId(userId: string | null): void {
  if (!session) return;
  session.userId = userId;
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* ignore */ }
}
